import React, { useEffect, useReducer, useState } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  TextField, 
  DialogActions, 
  Button, 
  Stack,
  Snackbar, 
  Alert 
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { CacheService } from "../../common/services/cache.service";
import environment from "../../environment/environment";
import APIService from "../../services/api.service";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useRecoilState } from "recoil";
import { tokenState } from "../../atoms/token";
import { useNavigate } from "react-router-dom";
import APIOptions from "../../models/apiOptions";
import { Asset } from "../../models/asset";
import { CircularProgress } from "@mui/material";

export function OrderDownloadDialog({row, handleCloseClick}: {row: any, handleCloseClick: any}) {
  const [open, setOpen] = React.useState(true); 
  const [order, setOrder] = React.useState(null); 
  const [parent, setParent] = React.useState(null); 
  const [files, setFiles] = React.useState([]);
  const [ticScanRequested, setTicScanRequested] = React.useState(false);
  const [token, setToken] = useRecoilState(tokenState);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg]   = useState("");

  const [form, setForm] = useReducer(
    (form, updates) => ({...form, ...updates}),
    {}
  );

  const statuses = [
    "Submitted",
    "Completed",
    "Working",
    "Cancelled"
  ];
  
  useEffect(() => {
    console.log('row', row);
    const url = `${environment.eticApiUrl}getorder/${row.id}`;
    const apiService = new APIService<any>(axios, new CacheService, url, token);

    apiService.getAnyAsync().then((res:any) => {
      console.log('orders', res);
      setOrder(res);
      getTicParent(res.detailItems[0].ticParentDocId);
      res.detailItems.forEach((item) => { 
        if(item.ticScanRequested) { 
          setTicScanRequested(true);
        }
      });

      // sort detailitems so ticScanRequested is at the top
      let sort = res.detailItems.sort((a, b) => a.ticScanRequested ? -1 : 1);
      setFiles(sort);
    });

  }, []);

  const columns: GridColDef[] = [
    { field: 'ticParentDocId', headerName: 'Doc Id', width: 100 },
    { field: 'title', headerName: 'Title', width: 300 },
    { field: 'ticScanRequested', headerName: 'Scan Requested', width: 150 },
    { field: 'ticDocType', headerName: 'Doc Type', width: 100 },
    { field: 'ticParkAlpha', headerName: 'Park Alpha', width: 100 },
    { field: 'ticNumber', headerName: 'Tic #', width: 100 },
    
  ];

  const getTicParent = (ticParentDocId) => { 
    const url = `${environment.eticApiUrl}getparent/${ticParentDocId}`;
    const apiOptions: APIOptions = new APIOptions(url, Asset);
    const T = apiOptions.T;

    const apiService = new APIService<typeof T>(axios, new CacheService, apiOptions.url, token);
    apiService.getAnyAsync().then((res: any) => { 
      setParent(res);
    });
  }

  const handleRowClick = (rowArgs, event) => {    
    console.log('rowargs', rowArgs);
    const url = `${environment.eticApiUrl}getparent/${rowArgs.row.ticParentDocId}`;
    const apiOptions: APIOptions = new APIOptions(url, Asset);
    const T = apiOptions.T;

    const apiService = new APIService<typeof T>(axios, new CacheService, apiOptions.url, token);
    apiService.getAnyAsync().then((res: any) => { 
      setParent(res);
      const url = `${environment.webUrl}record/${res.ticParentDocId}`;
      //const url = `http://localhost:3000/record/${res.ticParentDocId}`;
      window.open(url, '_blank');
    });
   
  }

  const handleDownloadClick = async () => {
    setLoadingDownload(true);
    const url = `${environment.eticApiUrl}downloadorderzip/${row.id}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      setError('Download failed');
      setSnackbarMsg("Download failed, please try again.");
      setSnackbarOpen(true);
      console.error(`Download failed: ${response.body}`);        
      setLoadingDownload(false);
      return;
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);

    const contentDisposition = response.headers.get("Content-Disposition") || "";
    let fileName = "order-zip";

    const match = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i);

    if (match) {
      fileName = decodeURIComponent(match[1] || match[2] || match[3]);
    }
    
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = fileName; // or get filename from response header
    document.body.appendChild(a);
    a.click();
    a.remove();
    
    setLoadingDownload(false);
  }

  return (
    <div>
      <Dialog open={open}  PaperProps={{ style: { minWidth: "900px" } }}>
        {/* toast */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity="error"
            sx={{ width: "100%" }}
          >
            {snackbarMsg}
          </Alert>
        </Snackbar>

        <DialogTitle sx={{ fontWeight: "bolder" }}>Order Info</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "rgba(63, 66, 66, 0.76)", whiteSpace: "pre-line" }}>
            {
              <>
                <Stack spacing={2} direction="row" sx={{marginBottom: 2, marginTop: 2}}>
                  <TextField
                    type="text"
                    variant='outlined'
                    color='secondary'
                    label="Cust. Name"
                    disabled={true}
                    value={row?.custName}
                    sx={{width: 300}} 
                    size="small"
                    InputLabelProps={{shrink: true}}
                  />

                  <TextField
                    type="text"
                    variant='outlined'
                    color='secondary'
                    label="Cust. Email"
                    disabled={true}
                    value={row?.email}
                    sx={{width: 300}} 
                    size="small"
                    InputLabelProps={{shrink: true}}
                  />      

                  <TextField
                    type="text"
                    variant='outlined'
                    color='secondary'
                    label="Status"
                    disabled={true}
                    value={row?.status}
                    sx={{width: 150}} 
                    size="small"
                    InputLabelProps={{shrink: true}}
                  />      

                  <TextField
                    type="text"
                    variant='outlined'
                    color='secondary'
                    label="Date"
                    disabled={true}
                    value={row?.date}
                    sx={{width: 150}} 
                    size="small"
                    InputLabelProps={{shrink: true}}
                  />      
                </Stack>

                <Stack spacing={2} direction="row" sx={{marginBottom: 2, marginTop: 2}}>
                  <TextField
                    type="text"
                    variant='outlined'
                    color='secondary'
                    label="Staff Name"
                    disabled={true}
                    value={row?.staffName}
                    sx={{width: 200}} 
                    size="small"
                    InputLabelProps={{shrink: true}}
                  />

                  <TextField
                    type="text"
                    variant='outlined'
                    color='secondary'
                    label="Staff Email"
                    disabled={true}
                    value={row?.staffEmail}
                    sx={{width: 200}} 
                    size="small"
                    InputLabelProps={{shrink: true}}
                  />      
                </Stack>

                <h2>Files Packaged in ZIP</h2>
                { ticScanRequested &&
                  <h4><i style={{color: "black"}}>Warning there are items contained in this order that dont have scans attached. Please reach out to tic staff for more information.</i></h4>
                }
                <div className="center" style={{ height: 300, width: '100%' }}>
                  <DataGrid
                    getRowHeight={(params) => 'auto'}
                    sx={{ '.MuiDataGrid-columnHeaderTitle': {fontWeight: 'bolder'},
                    '.MuiDataGrid-columnHeaderTitleContainer': {borderBottom: 1 },
                    '.MuiDataGrid-row': {cursor: 'pointer', borderBottom: 1},
                    '.MuiDataGrid-iconButtonContainer': {visibility: 'visible' },
                    '.MuiDataGrid-sortIcon': {opacity: 'inherit !important' }
                    }}
                    rows={files.map((result, index) => (
                      {
                        id: result?.id,
                        ticParentDocId: result?.ticParentDocId,
                        title: result?.title,
                        ticScanRequested: result?.ticScanRequested ? 'Yes' : ' ',
                        ticDocType: result?.ticDocType,
                        ticParkAlpha: result?.ticParkAlpha,
                        ticNumber: result?.ticNumber
                      }
                    )) || []}
                    columns={columns}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 5 },
                      },
                    }}
                    onRowClick={handleRowClick}
                    pageSizeOptions={[5, 10, 25, 50]}
                    loading={files.length === 0}
                  />
                </div>
              </> 
              
            }
            
          </DialogContentText>          
        </DialogContent>

        <DialogActions>
          <Stack spacing={1} direction="column" sx={{marginTop: 2}}>
            {error && <DialogContentText sx={{ color: "red" }}>{snackbarMsg}</DialogContentText>}

            <Stack spacing={2} direction="row" sx={{marginBottom: 2, marginTop: 2}}>
              <Button
                id="secondary" 
                variant="outlined" 
                startIcon={<FontAwesomeIcon icon={["fas", "circle-x"]} />}
                onClick={handleCloseClick}
                disabled={loadingDownload}>
                  Cancel
              </Button>

              <Button
                id="secondary" 
                variant="outlined" 
                onClick={handleDownloadClick}
                disabled={loadingDownload}
                startIcon={
                  loadingDownload
                    ? <CircularProgress size={20} />
                    : <FontAwesomeIcon icon={["fas", "download"]} />
                }
              >
              {loadingDownload ? "Downloading…" : "Download"}
              </Button>
            </Stack>
          </Stack>
         
          
        </DialogActions>

      </Dialog>
    </div>
  );
}
